import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { parseFile, validateRows } from './utils';
import { useSettings } from '../../utils/AppSettingsContext';

const DataImporter = ({ entity, requiredFields = [], onCommit, onComplete, importErrorMessage = '' }) => {
  const [rows, setRows] = useState([]);
  const [, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [recordCount, setRecordCount] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressType, setProgressType] = useState('import');
  const [toastMsg, setToastMsg] = useState('');
  const maxRecordLoad = useSettings('max_record_load');
  const allRowsInvalid = useMemo(() => rows.every((r) => Object.keys(r._errors).length > 0), [rows]);
  const hasBlockingImport = useMemo(() => rows.some((r) => r._blockImport), [rows]);
  const commitDisabled = allRowsInvalid || hasBlockingImport;

  useEffect(() => {
    if (toastMsg) {
      const t = setTimeout(() => setToastMsg(''), 5000);
      return () => clearTimeout(t);
    }
  }, [toastMsg]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    setProgress(0);
    setProgressType('validate');
    setShowProgress(true);
    try {
      const parsed = await parseFile(file);
      const limit = Number(maxRecordLoad) || parsed.length;
      const rowsToValidate = parsed.slice(0, limit);
      setRecordCount(rowsToValidate.length);
      if (parsed.length > limit) {
        setToastMsg(
          `You can only load ${limit} at a time. Only the first ${limit} will be loaded in this batch.`
        );
      }
      const validated = await validateRows(
        rowsToValidate,
        requiredFields,
        entity,
        (count) => setProgress(count)
      );
      setRows(validated);
    } catch (err) {
      console.error(err);
      alert('Failed to parse file');
    } finally {
      setLoading(false);
      setShowProgress(false);
    }
  };

  const commit = async () => {
    const validRows = rows.filter((r) => Object.keys(r._errors).length === 0);
    setRecordCount(validRows.length);
    setProgress(0);
    setProgressType('import');
    setShowProgress(true);
    if (onCommit) {
      await onCommit(validRows, (count) => setProgress(count));
    }
    setShowProgress(false);
    setShowSuccess(true);
  };

  const visibleKeys = useMemo(() => {
    const keys = new Set();
    rows.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (!key.startsWith('_')) {
          keys.add(key);
        }
      });
    });
    return Array.from(keys);
  }, [rows]);

  return (
    <div>
      {toastMsg && (
        <div
          className="toast-container position-fixed top-0 start-50 translate-middle-x p-3"
          style={{ zIndex: 2000 }}
        >
          <div className="toast show text-bg-warning border-0">
            <div className="d-flex align-items-center justify-content-between">
              <div className="toast-body mb-0">{toastMsg}</div>
              <button
                type="button"
                className="btn-close btn-close-white ms-3 me-2"
                aria-label="Close"
                onClick={() => setToastMsg('')}
              ></button>
            </div>
          </div>
        </div>

      )}
      <div className="d-flex mb-32">
        <Form.Group controlId="import-file" className="w-25 me-2">
          <Form.Control type="file" accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFile} />
          {importErrorMessage && (
            <div className="alert alert-danger mt-2 mb-0" role="alert">
              {importErrorMessage}
            </div>
          )}
        </Form.Group>
        {rows.length > 0 && (
          <Button className="ms-auto" onClick={commit} disabled={commitDisabled}>
            <Icon icon='mage:upload' className='icon text-xl line-height-1 me-8' style={{ position: 'relative', top: '-3' }} />
            Start Import
          </Button>
        )}
      </div>

      {hasBlockingImport && (
        <div className="alert alert-danger py-2" role="alert">
          Resolve the highlighted manufacturer and class lookup errors before importing assets.
        </div>
      )}

      {showProgress && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <p className="text-xl fw-bold">
                  {progressType === 'validate' ? 'Validating Records' : 'Importing Records'}
                </p>
                <ProgressBar
                  now={recordCount ? (progress / recordCount) * 100 : 0}
                  label={`${progress}/${recordCount}`}
                  variant="primary"
                  animated
                  striped
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="w-100" style={{ overflowX: "auto" }}>
        {rows.length > 0 && (
          <>
            <Table bordered size="sm" className="mb-3">
              <thead>
                <tr>
                  {visibleKeys.map((key) => (
                    <th key={key}>{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx} className={Object.keys(row._errors).length > 0 ? 'table-danger' : ''}>
                    {visibleKeys.map((key) => (
                      <td key={key}>
                        {row[key]}
                        {row._errors[key] && (
                          <div className="text-danger small">{row._errors[key]}</div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </div>

      {showSuccess && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-2">
              <div className="modal-body text-center">
                <p className="text-xl fw-bold">Complete</p>
                Loaded {recordCount} records successfully
              </div>
              <div className="modal-footer d-flex justify-content-center">
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowSuccess(false);
                    if (onComplete) onComplete();
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataImporter;
